import React from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    createProjectCustomField,
    deleteProjectCustomField,
    exportProjectCustomFieldGroup,
    getProjectCustomFields,
    importProjectCustomFieldGroup,
    updateProjectCustomField,
} from '../../../../../helpers/project-helper';

const FIELD_TYPES = [
    { value: 'SHORT_TEXT', label: 'Texto corto' },
    { value: 'LONG_TEXT', label: 'Texto largo' },
    { value: 'NUMBER', label: 'Numérico' },
    { value: 'LIST', label: 'Lista' },
    { value: 'CHECKBOX', label: 'Checkbox' },
];

const getInitialForm = () => ({
    name: '',
    key: '',
    fieldGroup: '',
    groupName: '',
    type: 'SHORT_TEXT',
    required: false,
    optionsText: '',
});

const getInitialConfirmState = () => ({
    isOpen: false,
    mode: '',
    groupId: '',
    groupName: '',
    fieldId: '',
    fieldName: '',
    importData: null,
    targetGroupName: '',
});

const mapFieldToForm = (field) => ({
    name: field?.name || '',
    key: field?.key || '',
    fieldGroup: field?.fieldGroup?._id || '',
    groupName: '',
    type: field?.type || 'SHORT_TEXT',
    required: Boolean(field?.required),
    optionsText: Array.isArray(field?.options) ? field.options.join('\n') : '',
});

const parseOptions = (optionsText = '') => {
    return String(optionsText)
        .split('\n')
        .map((option) => option.trim())
        .filter(Boolean);
};

const renderOptionsCell = (options = []) => {
    if (!Array.isArray(options) || options.length === 0) {
        return '-';
    }

    return (
        <div className="d-flex flex-wrap gap-1" style={{ maxWidth: '380px' }}>
            {options.map((option, index) => (
                <span
                    key={`${option}-${index}`}
                    className="badge bg-light text-dark border"
                    style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                    {option}
                </span>
            ))}
        </div>
    );
};

export const ProjectCustomFieldsTabContent = ({ projectId }) => {
    const { token } = useSelector((state) => state.auth);
    const importFileInputRef = React.useRef(null);

    const [customFields, setCustomFields] = React.useState([]);
    const [customFieldGroups, setCustomFieldGroups] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [deletingFieldId, setDeletingFieldId] = React.useState('');
    const [error, setError] = React.useState('');
    const [form, setForm] = React.useState(getInitialForm());
    const [editingFieldId, setEditingFieldId] = React.useState('');
    const [confirmState, setConfirmState] = React.useState(getInitialConfirmState());
    const [isImportingGroup, setIsImportingGroup] = React.useState(false);
    const [exportingGroupId, setExportingGroupId] = React.useState('');

    const loadCustomFields = React.useCallback(async () => {
        if (!token || !projectId) {
            setError('No se pudo obtener los campos personalizados del proyecto.');
            setCustomFields([]);
            setCustomFieldGroups([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');

        const resp = await getProjectCustomFields(token, projectId);

        if (!resp?.success) {
            setError(resp?.message || 'No se pudo obtener los campos personalizados del proyecto.');
            setCustomFields([]);
            setCustomFieldGroups([]);
            setIsLoading(false);
            return;
        }

        const nextCustomFields = Array.isArray(resp?.data?.customFields)
            ? resp.data.customFields
            : (Array.isArray(resp?.data) ? resp.data : []);
        const nextCustomFieldGroups = Array.isArray(resp?.data?.customFieldGroups)
            ? resp.data.customFieldGroups
            : [];

        setCustomFields(nextCustomFields);
        setCustomFieldGroups(nextCustomFieldGroups);
        setIsLoading(false);
    }, [token, projectId]);

    React.useEffect(() => {
        loadCustomFields();
    }, [loadCustomFields]);

    const handleInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const closeConfirmModal = () => {
        setConfirmState(getInitialConfirmState());
    };

    const downloadJson = (filename, data) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleExportClick = (groupInfo) => {
        setConfirmState({
            isOpen: true,
            mode: 'export',
            groupId: groupInfo.groupId,
            groupName: groupInfo.groupName,
            fieldId: '',
            fieldName: '',
            importData: null,
            targetGroupName: '',
        });
    };

    const handleStartImport = () => {
        if (importFileInputRef.current) {
            importFileInputRef.current.click();
        }
    };

    const handleImportFileChange = async (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        try {
            const fileContent = await file.text();
            const parsedData = JSON.parse(fileContent);
            const parsedFields = Array.isArray(parsedData?.customFields) ? parsedData.customFields : [];

            if (parsedFields.length === 0) {
                setError('El archivo JSON no contiene campos personalizados válidos para importar.');
                event.target.value = '';
                return;
            }

            const sourceGroupName = String(parsedData?.group?.name || '').trim();

            setConfirmState({
                isOpen: true,
                mode: 'import',
                groupId: '',
                groupName: sourceGroupName || 'Grupo importado',
                fieldId: '',
                fieldName: '',
                importData: parsedData,
                targetGroupName: sourceGroupName,
            });
            setError('');
        } catch (fileError) {
            setError('No se pudo leer el archivo JSON. Verifica que tenga un formato válido.');
        }

        event.target.value = '';
    };

    const handleConfirmChange = (event) => {
        const { name, value } = event.target;
        setConfirmState((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleConfirmAction = async () => {
        if (!token || !projectId) {
            return;
        }

        if (confirmState.mode === 'export') {
            if (!confirmState.groupId || exportingGroupId) {
                return;
            }

            setExportingGroupId(confirmState.groupId);
            setError('');
            const exportingToastId = toast.loading('Exportando grupo de campos...');

            const resp = await exportProjectCustomFieldGroup(token, projectId, confirmState.groupId);

            if (!resp?.success || !resp?.data) {
                const message = resp?.message || 'No se pudo exportar el grupo de campos personalizados.';
                setError(message);
                toast.update(exportingToastId, {
                    render: message,
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000,
                    closeOnClick: true,
                });
                setExportingGroupId('');
                closeConfirmModal();
                return;
            }

            const safeGroupName = String(confirmState.groupName || 'grupo')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_+|_+$/g, '') || 'grupo';
            downloadJson(`custom-fields-${safeGroupName}.json`, resp.data);
            toast.update(exportingToastId, {
                render: 'Grupo exportado correctamente.',
                type: 'success',
                isLoading: false,
                autoClose: 2500,
                closeOnClick: true,
            });
            setExportingGroupId('');
            closeConfirmModal();
            return;
        }

        if (confirmState.mode === 'import') {
            if (isImportingGroup || !confirmState.importData) {
                return;
            }

            setIsImportingGroup(true);
            setError('');
            const importingToastId = toast.loading('Importando grupo de campos...');

            const payload = {
                importData: confirmState.importData,
                targetGroupName: String(confirmState.targetGroupName || '').trim() || undefined,
            };

            const resp = await importProjectCustomFieldGroup(token, projectId, payload);

            if (!resp?.success) {
                const message = resp?.message || 'No se pudo importar el grupo de campos personalizados.';
                setError(message);
                toast.update(importingToastId, {
                    render: message,
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000,
                    closeOnClick: true,
                });
                setIsImportingGroup(false);
                closeConfirmModal();
                return;
            }

            await loadCustomFields();
            toast.update(importingToastId, {
                render: resp?.message || 'Grupo importado correctamente.',
                type: 'success',
                isLoading: false,
                autoClose: 2500,
                closeOnClick: true,
            });
            setIsImportingGroup(false);
            closeConfirmModal();
        }

        if (confirmState.mode === 'delete-field') {
            if (!confirmState.fieldId || deletingFieldId) {
                return;
            }

            setDeletingFieldId(confirmState.fieldId);
            setError('');
            const deletingToastId = toast.loading('Eliminando campo personalizado...');

            const resp = await deleteProjectCustomField(token, projectId, confirmState.fieldId);

            if (!resp?.success) {
                const message = resp?.message || 'No se pudo eliminar el campo personalizado.';
                setError(message);
                toast.update(deletingToastId, {
                    render: message,
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000,
                    closeOnClick: true,
                });
                setDeletingFieldId('');
                closeConfirmModal();
                return;
            }

            await loadCustomFields();

            if (editingFieldId === confirmState.fieldId) {
                resetForm();
            }

            toast.update(deletingToastId, {
                render: resp?.message || 'Campo eliminado correctamente.',
                type: 'success',
                isLoading: false,
                autoClose: 2500,
                closeOnClick: true,
            });
            setDeletingFieldId('');
            closeConfirmModal();
        }
    };

    const resetForm = () => {
        setForm(getInitialForm());
        setEditingFieldId('');
    };

    const handleEdit = (field) => {
        setEditingFieldId(field._id);
        setForm(mapFieldToForm(field));
        setError('');
    };

    const handleSave = async (event) => {
        event.preventDefault();

        if (!token || !projectId || isSaving) {
            return;
        }

        if (form.type === 'LIST') {
            const parsedOptions = parseOptions(form.optionsText);
            if (parsedOptions.length === 0) {
                setError('Debes agregar al menos una opción para el campo tipo lista.');
                return;
            }
        }

        setIsSaving(true);
        setError('');
        const savingToastId = toast.loading(editingFieldId ? 'Guardando cambios del campo...' : 'Creando campo personalizado...');

        const payload = {
            name: form.name.trim(),
            key: form.key.trim() || undefined,
            fieldGroup: form.fieldGroup || undefined,
            groupName: !form.fieldGroup ? form.groupName.trim() : undefined,
            type: form.type,
            required: Boolean(form.required),
            options: form.type === 'LIST' ? parseOptions(form.optionsText) : [],
        };

        if (!payload.fieldGroup && !payload.groupName) {
            setError('Debes seleccionar un grupo existente o escribir uno nuevo.');
            setIsSaving(false);
            return;
        }

        const resp = editingFieldId
            ? await updateProjectCustomField(token, projectId, editingFieldId, payload)
            : await createProjectCustomField(token, projectId, payload);

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo guardar el campo personalizado.';
            setError(message);
            toast.update(savingToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsSaving(false);
            return;
        }

        await loadCustomFields();
        resetForm();
        toast.update(savingToastId, {
            render: resp?.message || (editingFieldId ? 'Campo actualizado correctamente.' : 'Campo creado correctamente.'),
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsSaving(false);
    };

    const handleDelete = async (fieldId) => {
        if (!token || !projectId || deletingFieldId) {
            return;
        }

        const selectedField = customFields.find((item) => item?._id === fieldId);
        setConfirmState({
            isOpen: true,
            mode: 'delete-field',
            groupId: '',
            groupName: '',
            fieldId,
            fieldName: selectedField?.name || '',
            importData: null,
            targetGroupName: '',
        });
    };

    const formatType = (type) => {
        return FIELD_TYPES.find((item) => item.value === type)?.label || type || '-';
    };

    const groupedCustomFields = React.useMemo(() => {
        const grouped = customFields.reduce((accumulator, field) => {
            const groupId = field?.fieldGroup?._id || `no_group_${field?._id || Math.random().toString(36).slice(2)}`;
            const groupName = field?.fieldGroup?.name || 'Sin grupo';

            if (!accumulator[groupId]) {
                accumulator[groupId] = {
                    groupId,
                    groupName,
                    fields: [],
                };
            }

            accumulator[groupId].fields.push(field);
            return accumulator;
        }, {});

        return Object.values(grouped).sort((groupA, groupB) => groupA.groupName.localeCompare(groupB.groupName));
    }, [customFields]);

    return (
        <div>
            <div className="mb-4 d-flex align-items-center justify-content-between">
                <h5 className="fw-bold mb-0">Campos personalizados del proyecto</h5>
                <>
                    <input
                        ref={importFileInputRef}
                        type="file"
                        className="d-none"
                        accept="application/json,.json"
                        onChange={handleImportFileChange}
                    />
                    <button
                        type="button"
                        className="btn btn-sm btn-light-brand"
                        onClick={handleStartImport}
                        disabled={isImportingGroup}
                    >
                        {isImportingGroup ? 'Importando...' : 'Importar grupo (JSON)'}
                    </button>
                </>
            </div>

            {error && (
                <div className="alert alert-danger py-2" role="alert">{error}</div>
            )}

            <div className="card border mb-4">
                <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6 className="fw-semibold mb-0">{editingFieldId ? 'Editar campo' : 'Nuevo campo'}</h6>
                        {editingFieldId && (
                            <button type="button" className="btn btn-light-brand btn-sm" onClick={resetForm}>
                                Cancelar edición
                            </button>
                        )}
                    </div>

                    <form onSubmit={handleSave}>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label" htmlFor="custom-field-name">Nombre del campo</label>
                                <input
                                    id="custom-field-name"
                                    name="name"
                                    className="form-control"
                                    value={form.name}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label" htmlFor="custom-field-key">Clave (opcional)</label>
                                <input
                                    id="custom-field-key"
                                    name="key"
                                    className="form-control"
                                    value={form.key}
                                    onChange={handleInputChange}
                                    placeholder="ejemplo: numero_serie"
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label" htmlFor="custom-field-group">Grupo de campos</label>
                                <select
                                    id="custom-field-group"
                                    name="fieldGroup"
                                    className="form-select form-select-sm"
                                    value={form.fieldGroup}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Crear grupo nuevo</option>
                                    {customFieldGroups.map((group) => (
                                        <option key={group._id} value={group._id}>{group.name}</option>
                                    ))}
                                </select>
                            </div>
                            {!form.fieldGroup && (
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="custom-field-group-name">Nombre del nuevo grupo</label>
                                    <input
                                        id="custom-field-group-name"
                                        name="groupName"
                                        className="form-control"
                                        value={form.groupName}
                                        onChange={handleInputChange}
                                        placeholder="Ejemplo: Datos Cliente"
                                        required={!form.fieldGroup}
                                    />
                                </div>
                            )}
                            <div className="col-md-6">
                                <label className="form-label" htmlFor="custom-field-type">Tipo</label>
                                <select
                                    id="custom-field-type"
                                    name="type"
                                    className="form-select form-select-sm"
                                    value={form.type}
                                    onChange={handleInputChange}
                                >
                                    {FIELD_TYPES.map((type) => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-6 d-flex align-items-end">
                                <div className="form-check mb-2">
                                    <input
                                        id="custom-field-required"
                                        name="required"
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={Boolean(form.required)}
                                        onChange={handleInputChange}
                                    />
                                    &nbsp;<label className="form-check-label" htmlFor="custom-field-required">Campo obligatorio</label>
                                </div>
                            </div>

                            {form.type === 'LIST' && (
                                <div className="col-12">
                                    <label className="form-label" htmlFor="custom-field-options">Opciones (una por línea)</label>
                                    <textarea
                                        id="custom-field-options"
                                        name="optionsText"
                                        className="form-control"
                                        rows={4}
                                        value={form.optionsText}
                                        onChange={handleInputChange}
                                        placeholder={'Alta\nMedia\nBaja'}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="d-flex justify-content-end mt-3">
                            <button type="submit" className="btn btn-primary btn-sm" disabled={isSaving}>
                                {isSaving ? 'Guardando...' : editingFieldId ? 'Guardar cambios' : 'Crear campo'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {isLoading && <div className="py-3 text-muted">Cargando campos...</div>}

            {!isLoading && customFields.length === 0 && (
                <div className="py-3 text-muted">No hay campos personalizados configurados para este proyecto.</div>
            )}

            {!isLoading && customFields.length > 0 && (
                <div className="d-flex flex-column gap-3">
                    {groupedCustomFields.map((groupInfo) => (
                        <div className="card border" key={groupInfo.groupId}>
                            <div className="card-header py-2 d-flex align-items-center justify-content-between gap-2">
                                <h6 className="mb-0 fw-semibold">{groupInfo.groupName}</h6>
                                {groupInfo.groupId && !groupInfo.groupId.startsWith('no_group_') && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light-brand"
                                        onClick={() => handleExportClick(groupInfo)}
                                        disabled={Boolean(exportingGroupId)}
                                    >
                                        {exportingGroupId === groupInfo.groupId ? 'Exportando...' : 'Exportar JSON'}
                                    </button>
                                )}
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-bordered mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Nombre</th>
                                                <th>Clave</th>
                                                <th>Tipo</th>
                                                <th>Obligatorio</th>
                                                <th>Opciones</th>
                                                <th style={{ width: '190px' }}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {groupInfo.fields.map((field) => (
                                                <tr key={field._id}>
                                                    <td>{field?.name || '-'}</td>
                                                    <td>{field?.key || '-'}</td>
                                                    <td>{formatType(field?.type)}</td>
                                                    <td>{field?.required ? 'Sí' : 'No'}</td>
                                                    <td>{renderOptionsCell(field?.options)}</td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => handleEdit(field)}
                                                            >
                                                                Editar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger btn-sm"
                                                                onClick={() => handleDelete(field._id)}
                                                                disabled={deletingFieldId === field._id}
                                                            >
                                                                {deletingFieldId === field._id ? 'Eliminando...' : 'Eliminar'}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {confirmState.isOpen && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirmar acción</h5>
                                    <button type="button" className="btn-close" aria-label="Close" onClick={closeConfirmModal} />
                                </div>
                                <div className="modal-body">
                                    {confirmState.mode === 'export' && (
                                        <p className="mb-0">
                                            ¿Deseas exportar el grupo <strong>{confirmState.groupName || 'Sin nombre'}</strong> en formato JSON?
                                        </p>
                                    )}

                                    {confirmState.mode === 'import' && (
                                        <>
                                            <p className="mb-2">
                                                ¿Deseas importar este grupo de campos personalizados al proyecto actual?
                                            </p>
                                            <div className="mb-2">
                                                <span className="text-muted">Campos detectados: </span>
                                                <strong>{Array.isArray(confirmState.importData?.customFields) ? confirmState.importData.customFields.length : 0}</strong>
                                            </div>
                                            <label className="form-label" htmlFor="target-group-name-import">
                                                Nombre del grupo destino (opcional)
                                            </label>
                                            <input
                                                id="target-group-name-import"
                                                name="targetGroupName"
                                                className="form-control"
                                                value={confirmState.targetGroupName}
                                                onChange={handleConfirmChange}
                                                placeholder="Ejemplo: Datos Cliente"
                                            />
                                        </>
                                    )}

                                    {confirmState.mode === 'delete-field' && (
                                        <p className="mb-0">
                                            ¿Deseas eliminar el campo <strong>{confirmState.fieldName || 'seleccionado'}</strong>?
                                        </p>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-light-brand" onClick={closeConfirmModal}>
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className={confirmState.mode === 'delete-field' ? 'btn btn-danger' : 'btn btn-primary'}
                                        onClick={handleConfirmAction}
                                        disabled={Boolean(exportingGroupId) || isImportingGroup || Boolean(deletingFieldId)}
                                    >
                                        {confirmState.mode === 'export'
                                            ? 'Confirmar exportación'
                                            : confirmState.mode === 'import'
                                                ? 'Confirmar importación'
                                                : 'Eliminar campo'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" />
                </>
            )}
        </div>
    );
};
